import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCompanies } from '../services/api';
import { Copy, Check, Code, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WidgetDemoPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedType, setSelectedType] = useState('combo');
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    // Load widget script
    if (selectedCompany) {
      const existingScript = document.getElementById('fv-widget-script');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.id = 'fv-widget-script';
      script.src = `${BACKEND_URL}/api/widget/script.js`;
      script.async = true;
      script.onload = () => {
        if (window.FairViewsWidget) {
          window.FairViewsWidget.init();
        } else if (window.TrustpilotWidget) {
          window.TrustpilotWidget.init();
        }
      };
      document.body.appendChild(script);
      
      // Set URLs for the widget (support both naming conventions)
      window.FAIRVIEWS_WIDGET_URL = BACKEND_URL;
      window.FAIRVIEWS_REVIEW_URL = window.location.origin;
      window.TRUSTPILOT_WIDGET_URL = BACKEND_URL;
      window.TRUSTPILOT_REVIEW_URL = window.location.origin;
    }
  }, [selectedCompany, selectedType]);

  const loadCompanies = async () => {
    try {
      const data = await getCompanies();
      setCompanies(data.filter(c => c.domain !== 'aleccstore.fr'));
      if (data.length > 0) {
        setSelectedCompany(data.find(c => c.domain !== 'aleccstore.fr') || data[0]);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const widgetTypes = [
    { id: 'combo', name: 'Combo', description: 'Widget complet avec avis' },
    { id: 'banner', name: 'Bandeau', description: 'Bandeau horizontal compact' },
    { id: 'badge', name: 'Badge', description: 'Badge vertical avec score' },
    { id: 'mini', name: 'Mini', description: 'Badge ultra compact' },
  ];

  const getEmbedCode = () => {
    if (!selectedCompany) return '';
    return `<!-- Widget FairViews -->
<div 
  id="fairviews-widget-${selectedType}"
  data-domain="${selectedCompany.domain}">
</div>
<script>
  window.FAIRVIEWS_WIDGET_URL = "https://fairviews.com";
  window.FAIRVIEWS_REVIEW_URL = "https://fairviews.com";
</script>
<script src="https://fairviews.com/api/widget/script.js" defer></script>`;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#1a1a1a] mb-4">
            Widgets FairViews
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Intégrez facilement vos avis clients sur votre site web avec nos widgets personnalisables.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border p-6 sticky top-24">
              <h2 className="font-semibold text-[#1a1a1a] mb-4">Configuration</h2>
              
              {/* Company Select */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entreprise
                </label>
                <select
                  value={selectedCompany?.domain || ''}
                  onChange={(e) => setSelectedCompany(companies.find(c => c.domain === e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00b67a] focus:border-transparent"
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.domain}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Widget Type Select */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de widget
                </label>
                <div className="space-y-2">
                  {widgetTypes.map((type) => (
                    <label
                      key={type.id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedType === type.id
                          ? 'border-[#00b67a] bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="widgetType"
                        value={type.id}
                        checked={selectedType === type.id}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="sr-only"
                      />
                      <div>
                        <p className="font-medium text-[#1a1a1a]">{type.name}</p>
                        <p className="text-sm text-gray-500">{type.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Embed Code */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Code d'intégration
                  </label>
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-1 text-sm text-[#00b67a] hover:text-[#00a36c] transition-colors"
                  >
                    {copied ? (
                      <><Check className="w-4 h-4" /> Copié !</>
                    ) : (
                      <><Copy className="w-4 h-4" /> Copier</>
                    )}
                  </button>
                </div>
                <pre className="bg-gray-900 text-gray-100 text-xs p-4 rounded-lg overflow-x-auto max-h-48">
                  <code>{getEmbedCode()}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold text-[#1a1a1a]">Aperçu</h2>
                <span className="text-sm text-gray-500">
                  {selectedCompany?.name}
                </span>
              </div>

              {/* Widget Preview */}
              <div className="bg-gray-100 rounded-lg p-8 min-h-[300px] flex items-center justify-center">
                {selectedCompany && (
                  <div
                    key={`${selectedCompany.domain}-${selectedType}`}
                    id={`fairviews-widget-${selectedType}`}
                    data-domain={selectedCompany.domain}
                  >
                    <div className="animate-pulse flex items-center gap-2">
                      <div className="h-4 w-4 bg-gray-300 rounded"></div>
                      <div className="h-4 w-32 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  Instructions d'intégration
                </h3>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Copiez le code ci-dessus</li>
                  <li>Collez-le dans le HTML de votre site, à l'endroit où vous souhaitez afficher le widget</li>
                  <li>Le widget se chargera automatiquement avec les dernières données</li>
                </ol>
              </div>
            </div>

            {/* All Widgets Preview */}
            <div className="bg-white rounded-lg border p-6 mt-6">
              <h2 className="font-semibold text-[#1a1a1a] mb-6">Tous les widgets</h2>
              
              <div className="space-y-8">
                {selectedCompany && widgetTypes.map((type) => (
                  <div key={type.id}>
                    <h3 className="text-sm font-medium text-gray-500 mb-3">{type.name}</h3>
                    <div className="bg-gray-50 rounded-lg p-6">
                      <div
                        id={`fairviews-preview-${type.id}`}
                        data-domain={selectedCompany.domain}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WidgetDemoPage;
